import React, { useState, useEffect, useRef, useMemo } from "react";
import { MessageSquare, X } from "lucide-react";
import { Comment, CommentIcon, CommentResolutionStatus } from "../types/prospect";
import CommentList from "./CommentList";
import CommentEditor from "./CommentEditor";
import { useOutsideClickRef } from "../hooks/useOutsideClickRef";

interface CommentPopupProps {
  comments: Comment[];
  onSave?: (comment: string, mentions?: string[], icon?: CommentIcon) => void;
  onResolve?: (commentId: string) => void;
  onClose: () => void;
  isOpen: boolean;
  relationshipManagers?: any[];
  currentUser?: string;
}

const CommentPopup: React.FC<CommentPopupProps> = ({
  comments = [],
  onSave,
  onResolve,
  onClose,
  isOpen,
  relationshipManagers = [],
  currentUser = "You"
}) => {

  const sortedInitialComments = useMemo(() => 
    [...comments].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    ), 
    [comments]
  );
  
  const [localComments, setLocalComments] = useState<Comment[]>(sortedInitialComments);
  
  const popupRef = useOutsideClickRef<HTMLDivElement>(onClose);
  const commentsContainerRef = useRef<HTMLDivElement>(null);

  // Update local comments when prop comments change
    useEffect(() => {
    // Sort comments by date in ascending order (oldest first)
    const sorted = [...comments].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    setLocalComments(sorted);
  }, [comments]);

  // Scroll to the top of the comments container when new comments are added
  // (since we're displaying newest comments first)
   useEffect(() => {
    if (commentsContainerRef.current) {
      commentsContainerRef.current.scrollTop = commentsContainerRef.current.scrollHeight;
    }
  }, [localComments]);

  // Reset when popup opens or closes
 useEffect(() => {
    if (isOpen && commentsContainerRef.current) {
      // When popup opens, scroll to the bottom (newest comments)
      commentsContainerRef.current.scrollTop = commentsContainerRef.current.scrollHeight;
    }
  }, [isOpen]);

  const handleSaveComment = (text: string, mentions?: string[], icon?: CommentIcon) => {
    if (onSave && text) {
      onSave(text, mentions, icon);
      
      // Optimistically update UI with new comment
      const newComment: Comment = {
        id: `temp-${Date.now()}`,
        text,
        author: currentUser,
        date: new Date().toISOString(),
        mentions,
        icon,
        resolutionStatus: 'pending'
      };
      
      // Add new comment at the beginning of the array (newest first)
      setLocalComments(prev => [newComment, ...prev]);
    }
  };

  const handleResolveComment = (commentId: string) => {
    if (onResolve) {
      onResolve(commentId);
      
      // Optimistically update UI
      setLocalComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, resolutionStatus: 'resolved' as CommentResolutionStatus } 
            : comment
        )
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-end">
      <div
        ref={popupRef}
        className="bg-white w-full max-w-md h-full shadow-lg flex flex-col animate-slide-in-right rounded-l-lg border-l border-t border-b border-slate-200"
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold flex items-center text-slate-900">
            <MessageSquare className="w-5 h-5 mr-2 text-primary" /> Conversations
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 transition-colors"
            aria-label="Close comments"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div 
          ref={commentsContainerRef}
          className="flex-1 p-4 overflow-auto bg-slate-50"
        >
          {/* Comment List */}
          <CommentList 
            comments={localComments} 
            onResolveComment={handleResolveComment}
            currentUser={currentUser}
            relationshipManagers={relationshipManagers}
          />
        </div>
        
        {/* Comment Editor */}
        <div className="p-4 bg-white border-t border-slate-200">
          <CommentEditor 
            onSave={handleSaveComment} 
            relationshipManagers={relationshipManagers} 
          />
        </div>
      </div>
    </div>
  );
};

export default CommentPopup;